import jwtDecode from "jwt-decode";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import RefreshButton from "../../../../common/Button/RefreshButton";
import VerifiedButton from "../../../../common/batches/VerifiedButton";
import ErrorHandler from "../../../../common/ErrorHandler";
import Filters from "../../../../common/Filters/Filters";
import SearchBoxWithCustomQuery from "../../../../common/SearchBoxWithCustomQuery";
import { errorToast } from "../../../../react-toastfiy/toast";
import stateService from "../../../../services/state.service";
import taService from "../services/ta.service";
import taUserManagementService from "../../../../services/channelFinance/taUserManagement.service";
import TARenewLimitModal from "../../../../components/tradeAdvance/TAUserManagement/TARenewLimitModal";
import useOnboardingView from "../hooks/useOnboardingView";
import AddCommentModal from "./AddCommentModal";
import CreateTAUserModal from "./CreateTAUserModal";
import OpenApplicationIDModal from "./OpenApplicationIDModal";
import OpenUpdateFinalLimit from "./OpenUpdateFinalLimit";

// Labels shown in the Filters drawer, mapped to the taLimitStatus the backend
// sends. "active" is deliberately absent: the filter exists to surface work,
// and a limit with more than 30 days left is not work.
export const LIMIT_EXPIRY_FILTER = {
  "Expiring soon": "expiring_soon",
  Expired: "expired",
};

export const limitExpiryMatches = (item, value, isChecked) =>
  Boolean(isChecked) && item?.taLimitStatus === LIMIT_EXPIRY_FILTER[value];

// Reuses the card's existing pill palette so the expiry pill reads as native:
// the amber is the "Pending" pill's, the red is the "Rejected" pill's. An
// active limit gets a neutral slate rather than the "Verified" green, so a
// date that is merely informational does not read as a status to act on.
const LIMIT_EXPIRY_PILL = {
  active: { bg: "rgb(241,245,249)", color: "rgb(71,85,105)" },
  expiring_soon: { bg: "rgb(255, 251, 235)", color: "rgb(201, 131, 83)" },
  expired: { bg: "rgb(254,242,242)", color: "rgb(200,39,39)" },
};

// Which limits are worth a Renew button. Deliberately not the pill map: every
// case with a limit shows its expiry date, but only these need action.
export const RENEWABLE_LIMIT_STATUSES = ["expiring_soon", "expired"];

// Limit Setting Documents e-sign, from the backend's limitSettingEsignStatus.
// Same pending amber / verified green as the document count pills.
export const ESIGN_FILTER = {
  "E-Sign Pending": "pending",
  "E-Sign Completed": "completed",
};

export const esignMatches = (item, value, isChecked) =>
  Boolean(isChecked) && item?.limitSettingEsignStatus === ESIGN_FILTER[value];

const ESIGN_PILL = {
  pending: { label: "E-Sign Pending", bg: "rgb(255, 251, 235)", color: "rgb(201, 131, 83)" },
  completed: { label: "E-Sign Completed", bg: "rgb(214, 255, 214)", color: "rgb(0,125,13)" },
};

export function EsignPill({ status }) {
  const tone = ESIGN_PILL[status];
  if (!tone) return null;
  return (
    <span
      className="me-1 px-2 rounded-pill"
      style={{
        fontSize: "10px",
        paddingBottom: "2px",
        paddingTop: "1px",
        fontWeight: 600,
        backgroundColor: tone.bg,
        color: tone.color,
      }}
    >
      <i className="bi bi-pen me-1"></i>
      {tone.label}
    </span>
  );
}

export function LimitExpiryPill({ status, expiryDate, daysToExpiry }) {
  const tone = LIMIT_EXPIRY_PILL[status];
  if (!tone) return null;
  const when =
    typeof daysToExpiry !== "number"
      ? ""
      : daysToExpiry < 0
        ? ` \u00b7 ${Math.abs(daysToExpiry)} days ago`
        : daysToExpiry === 0
          ? " \u00b7 today"
          : ` \u00b7 in ${daysToExpiry} days`;
  return (
    <span
      className="me-1 px-2 rounded-pill"
      style={{
        fontSize: "10px",
        paddingBottom: "2px",
        paddingTop: "1px",
        fontWeight: 600,
        backgroundColor: tone.bg,
        color: tone.color,
      }}
    >
      {`Limit ${status === "expired" ? "expired" : "expires"} ${expiryDate || ""}${when}`}
    </span>
  );
}

const OnBoardingView = () => {
  const currentUser = localStorage.getItem("refreshToken");
  const user = jwtDecode(currentUser);
  const history = useHistory();
  const [selectedState, setSelectedState] = useState("All");
  const [stateOption, setStateOption] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const {
    data,
    filterData,
    setFilterData,
    setData,
    modal,
    setModal,
    userModal,
    setUserModal,
    modalData,
    setModalData,
    loading,
    handleViewDetails,
    handleVerifyAll,
    option,
    handleSelectStatusCode,
    statusCode,
    setStatusCode,
    handleSendEmail,
    openOutlookEmail,
    handleConformEmail,
    modalForUpdateLimit,
    setModalForUpdateLimit,
    modalAddComment,
    setModalAddComment,
    modalAddCommentData,
    setModalAddCommentData,
    modalForUpdateLimitData,
    setModalForUpdateLimitData,
  } = useOnboardingView();

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      if (user?.role === "Auction Representative") {
        const formData = new FormData();
        formData.append("caseStatus", statusCode);
        const response = await taService.GetAllTAUser(formData);

        if (response?.status === 200) {
          setFilterData(response?.data);
          setData(response?.data);
        } else {
          errorToast(response?.msg);
        }
      } else if (user?.role === "Business Analyst") {
        const formData = new FormData();
        formData.append("caseStatus", statusCode);
        const response = await taService.GetAllTAUserByBA(formData);
        if (response?.status === 200) {
          const updatedResponse = response?.data?.map((value) => ({
            ...value,
            ["stage"]: value?.isLimitSettingAllDocumentUploaded
              ? "Limit setting Email"
              : value?.isLosApplicationIdUpdated
                ? "Limit setting Document Upload"
                : value?.isCaseApprovedByBa
                  ? "LOS Updation"
                  : "Business Document Verification",
          }));
          setFilterData(updatedResponse);
          setData(updatedResponse);
        } else {
          errorToast(response?.msg);
        }
      } else if (
        user?.role === "Super Admin" ||
        user?.role === "Developer" ||
        user?.role === "Back Office HR"
      ) {
        const formData = new FormData();
        formData.append("caseStatus", statusCode);
        const response = await taService.GetAllTAUserBySA(formData);
        if (response?.status === 200) {
          setFilterData(response?.data);
          setData(response?.data);
        } else {
          errorToast(response?.msg);
        }
      } else if (user?.role === "Head - Risk And Process") {
        const formData = new FormData();
        formData.append("caseStatus", statusCode);
        const response = await taService.GetAllTAUserByRisk(formData);
        if (response?.status === 200) {
          setFilterData(response?.data);
          setData(response?.data);
        } else {
          errorToast(response?.msg);
        }
      }
    } catch (error) {
      errorToast(error?.msg || "Something went wrong");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await stateService.getAllState();

        if (response.length > 0) {
          const stateOption = response.map((val) => ({
            label: val?.state_name,
            value: val,
          }));

          setStateOption([{ label: "All", value: "all" }, ...stateOption]);
        } else {
          errorToast(response?.msg);
        }
      } catch (error) {
        errorToast(error?.msg || "Something went wrong");
      }
    };

    fetchData();
  }, []);

  const [renewUser, setRenewUser] = useState(null);
  const [renewLoadingId, setRenewLoadingId] = useState(null);
  const [filterOverride, setFilterOverride] = useState(null);

  // The card row carries the expiry status but not the limits the modal shows,
  // so fetch the full TA user first — the same fetch-then-open the expiry
  // dashboard uses. One modal, three surfaces.
  const openRenew = async (row) => {
    setRenewLoadingId(row?.taOnboardingCaseId);
    try {
      const res = await taUserManagementService.getTAUserDetail(row?.taUserId);
      if (res?.status === 200) {
        setRenewUser({
          ...res.data,
          ta_limit_status: res.data?.ta_limit_status ?? row?.taLimitStatus,
          ta_limit_days_to_expiry:
            res.data?.ta_limit_days_to_expiry ?? row?.taLimitDaysToExpiry,
        });
      } else {
        errorToast(res?.msg || "Failed to load TA user");
      }
    } catch (e) {
      errorToast("Failed to load TA user");
    } finally {
      setRenewLoadingId(null);
    }
  };

  // The Filters drawer filters client-side over whatever the selected queue
  // returned, and Pending is `is_email_sent_by_risk=False` — every expiring case
  // is past that point, so the filter would come back empty on the default
  // queue. Move to All, visibly, rather than showing nothing.
  const handleFilterSelectionChange = (selected) => {
    const picked = selected?.limitExpiry;
    const anyPicked = picked && Object.values(picked).some(Boolean);
    if (anyPicked && (statusCode === "pending" || statusCode === "rejected")) {
      setStatusCode("all");
    }
  };

  let filtersList = [];

  if (user?.role === "Business Analyst") {
    filtersList = [
      { key: "userState", name: "State", id: "state" },
      { key: "stage", name: "Stage", id: "stage" },
      {
        key: "isTAFundingByMT",
        name: "Funding Type",
        id: "fundingType",
        valueType: "boolean",
        booleanOptions: {
          true: {
            filterLabel: "Meratractor",
            filterValue: true,
          },
          false: {
            filterLabel: "Financier",
            filterValue: false,
          },
        },
      },
      {
        key: "taLimitStatus",
        name: "Limit Expiry",
        id: "limitExpiry",
        valueType: "computed",
        filterValues: Object.keys(LIMIT_EXPIRY_FILTER),
        computeFn: limitExpiryMatches,
      },
      {
        key: "limitSettingEsignStatus",
        name: "E-Sign",
        id: "esign",
        valueType: "computed",
        filterValues: Object.keys(ESIGN_FILTER),
        computeFn: esignMatches,
      },
    ];
  } else {
    filtersList = [
      { key: "userState", name: "State", id: "state" },
      {
        key: "isTAFundingByMT",
        name: "Funding Type",
        id: "fundingType",
        valueType: "boolean",
        booleanOptions: {
          true: {
            filterLabel: "Meratractor",
            filterValue: true,
          },
          false: {
            filterLabel: "Financier",
            filterValue: false,
          },
        },
      },
      // { key: "stage", name: "stage", id: "stage" },
    ];
  }

  try {
    return (
      <>
        {modal && (
          <OpenApplicationIDModal
            setshow={setModal}
            show={modal}
            data={modalData}
            onHide={() => {
              setModal(false);
            }}
          />
        )}
        {modalForUpdateLimit && (
          <OpenUpdateFinalLimit
            setshow={setModalForUpdateLimit}
            show={modalForUpdateLimit}
            data={modalForUpdateLimitData}
            onHide={() => {
              setModalForUpdateLimit(false);
            }}
          />
        )}
        {modalAddComment && (
          <AddCommentModal
            setshow={setModalAddComment}
            show={modalAddComment}
            data={modalAddCommentData}
            onHide={() => {
              setModalAddComment(false);
            }}
          />
        )}
        {renewUser && (
          <TARenewLimitModal
            show={Boolean(renewUser)}
            user={renewUser}
            onHide={() => setRenewUser(null)}
            onSuccess={() => {
              setRenewUser(null);
              // A renewed limit is no longer "expiring soon", so an active Limit
              // Expiry filter would hide the row the BA just renewed — and that
              // selection persists in localStorage, so it would stay hidden.
              // Clear it, with a fresh object so Filters picks the change up.
              setFilterOverride({ limitExpiry: {} });
              handleRefresh();
            }}
          />
        )}
        {userModal && (
          <CreateTAUserModal
            setshow={setUserModal}
            show={userModal}
            onHide={() => {
              setUserModal(false);
            }}
          />
        )}

        {loading || refreshing ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="container shadow-sm p-3 mb-2 border mt-4"
              style={{ borderRadius: "7px", backgroundColor: "#f4f4f4" }}
            >
              <div className="placeholder-glow">
                <div
                  className="mb-2"
                  style={{
                    width: "60%",
                    height: "20px",
                    backgroundColor: "#e0e0e0",
                  }}
                />
                <div
                  className="mb-2"
                  style={{
                    width: "80%",
                    height: "16px",
                    backgroundColor: "#e0e0e0",
                  }}
                />
                <div
                  className="mb-2"
                  style={{
                    width: "40%",
                    height: "16px",
                    backgroundColor: "#e0e0e0",
                  }}
                />
              </div>
            </div>
          ))
        ) : (
          <>
            {user.role !== "Auction Representative" && (
              <div className="container mt-3">
                <div className="d-flex justify-content-between">
                  <i
                    className="bi bi-arrow-left-circle"
                    onClick={() => history.goBack()}
                    style={{
                      fontSize: "xx-large",
                      color: "black",
                      borderRadius: "50%",
                      cursor: "pointer",
                    }}
                  ></i>
                </div>
              </div>
            )}
            <div className="container mt-3">
              <div className="d-flex flex-wrap align-items-center">
                <div className="col-12 col-md-6 d-flex justify-content-start mb-2 mb-md-0">
                  <h6 className="mb-0">TA User Onboarding Cases</h6>
                </div>
                {(user?.role === "Auction Representative" ||
                  user?.role === "Business Analyst") && (
                  <div className="col-12 col-md-6 d-flex justify-content-md-end justify-content-start mb-2 mb-md-0">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setUserModal(true)}
                    >
                      Start TA Onboarding
                    </button>
                  </div>
                )}
              </div>

              <div className="d-flex flex-wrap justify-content-between">
                <div className="col-12 col-md-4 col-lg-3 mb-2 mt-2">
                  <Select
                    options={option}
                    value={option.find((status) => {
                      return status?.value === statusCode;
                    })}
                    onChange={handleSelectStatusCode}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
                <div className=" d-flex justify-content-start justify-content-md-end align-items-center gap-2 flex-wrap col-12 col-md-7 col-lg-8 col-xl-6">
                  <SearchBoxWithCustomQuery
                    allData={data}
                    return_array={true}
                    setFilteredData={setFilterData}
                    keysToSearchOn={[
                      "companyName",
                      "personName",
                      "userState",
                      "losApplicationId",
                    ]}
                    useSearchBoxButton={true}
                  />
                  <Filters
                    data={data}
                    setData={setFilterData}
                    filteredData={filterData}
                    setFilteredData={setFilterData}
                    filtersList={filtersList}
                    localStorageKey="ta-onboarding-filters"
                    onSelectionChange={handleFilterSelectionChange}
                    externalSelectedFilters={filterOverride}
                  />
                  <RefreshButton
                    onClick={handleRefresh}
                    loadingData={refreshing}
                  />
                </div>
              </div>

              {!loading &&
                !refreshing &&
                Object.entries(filterData).length > 0 &&
                Object.entries(filterData).map(([keyBy, value], index) => (
                  <div
                    key={keyBy}
                    className="shadow-sm p-3 mb-2 border mt-3 bg-glass"
                    style={{
                      borderRadius: "7px",
                      borderLeft: value?.isTAFundingByMT
                        ? "4px solid rgb(13, 110, 253)"
                        : "none",
                    }}
                  >
                    <div className="col-12">
                      <LimitExpiryPill
                        status={value?.taLimitStatus}
                        expiryDate={value?.taLimitExpiryDate}
                        daysToExpiry={value?.taLimitDaysToExpiry}
                      />
                      <EsignPill status={value?.limitSettingEsignStatus} />
                      {RENEWABLE_LIMIT_STATUSES.includes(
                        value?.taLimitStatus,
                      ) &&
                        value?.taUserId &&
                        value?.isChannelFinanced && (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm py-0 px-2 me-1"
                            style={{ fontSize: "10px", fontWeight: 600 }}
                            disabled={renewLoadingId === value?.taOnboardingCaseId}
                            onClick={() => openRenew(value)}
                          >
                            {renewLoadingId === value?.taOnboardingCaseId
                              ? "Loading..."
                              : "Renew"}
                          </button>
                        )}
                      {value?.isCaseBusinessDetailsRejectedByBa && (
                        <span
                          className="me-1 px-2 rounded-pill"
                          style={{
                            fontSize: "13px",
                            paddingBottom: "2px",
                            paddingTop: "3px",
                            fontWeight: 500,
                            backgroundColor: "rgb(254,242,242)",
                            color: "rgb(200,39,39)",
                          }}
                        >
                          {`Rejected`}
                        </span>
                      )}
                      <div className="col-12  text-bold">
                        {value?.businessType === "individual"
                          ? `${value?.personName || "N/A"} - ${
                              value?.userState || ""
                            }`
                          : `${value?.companyName || "N/A"} - ${
                              value?.userState || ""
                            }`}
                        {value?.isTAFundingByMT && (
                          <span
                            className="ms-2 px-2 rounded-pill"
                            style={{
                              fontSize: "11px",
                              paddingBottom: "3px",
                              paddingTop: "3px",
                              fontWeight: 600,
                              backgroundColor: "rgb(232, 245, 255)",
                              color: "rgb(13, 110, 253)",
                              border: "1px solid rgb(184, 218, 255)",
                            }}
                          >
                            <i className="bi bi-building me-1"></i>MT Funded
                          </span>
                        )}
                      </div>

                      <div className="d-flex flex-wrap justify-content-between">
                        <div className="col-12 col-sm-6 col-md-5 col-lg-7 col-xl-5 col-xxl-6">
                          <span
                            className="mt-1 text-secondary"
                            // style={{ color: "rgb(192,190,190)" }}
                          >
                            {value?.personName || "N/A"}
                          </span>

                          <div className="d-flex flex-wrap justify-content-between mt-1 gap-1">
                            {(user?.role === "Auction Representative" ||
                              user?.role === "Business Analyst") &&
                            !value?.is_case_approved_by_ba ? (
                              <>
                                <div className="col-12 d-flex flex-wrap justify content-between">
                                  <span
                                    className="me-1 px-2 rounded-pill bold"
                                    style={{
                                      fontSize: "10px",
                                      paddingBottom: "2px",
                                      paddingTop: "1px",
                                      fontWeight: 500,
                                      backgroundColor: "rgb(254,242,242)",
                                      color: "rgb(200,39,39)",
                                    }}
                                  >
                                    {`${value?.missing || 0} Missing`}
                                  </span>
                                  <span
                                    className="me-1 px-2 rounded-pill"
                                    style={{
                                      fontSize: "10px",
                                      paddingBottom: "2px",
                                      paddingTop: "1px",
                                      fontWeight: 500,
                                      backgroundColor: "rgb(254,242,242)",
                                      color: "rgb(200,39,39)",
                                    }}
                                  >
                                    {`${value?.rejected || 0} Rejected`}
                                  </span>
                                  <span
                                    className="me-1 px-2 rounded-pill"
                                    style={{
                                      fontSize: "10px",
                                      paddingBottom: "2px",
                                      paddingTop: "1px",
                                      fontWeight: 500,
                                      backgroundColor: "rgb(255, 251, 235)",
                                      color: "rgb(201, 131, 83)",
                                    }}
                                  >
                                    {`${value?.pending || 0} Pending`}
                                  </span>
                                  <span
                                    className="me-1 px-2 rounded-pill"
                                    style={{
                                      fontSize: "10px",
                                      paddingBottom: "2px",
                                      paddingTop: "1px",
                                      fontWeight: 500,
                                      backgroundColor: "rgb(214, 255, 214)",
                                      color: "rgb(0,125,13)",
                                    }}
                                  >
                                    {`${value?.verified || 0} Verified`}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <VerifiedButton />
                            )}

                            {value?.isCaseApprovedByBa &&
                              value?.losApplicationId && (
                                <span
                                  style={{
                                    fontSize: "13px",
                                    color: "rgb(192,190,190)",
                                  }}
                                >
                                  LOS Application Id :
                                  <b> {value?.losApplicationId}</b>
                                </span>
                              )}
                          </div>
                        </div>

                        <div className="col-12 col-sm-6 col-md-7 col-lg-5 d-flex flex-wrap justify-content-end gap-1">
                          {value?.adminApprovalStatus === "approved" ? (
                            <>
                              <div className="" style={{ marginTop: "10px" }}>
                                {!value?.isCaseBusinessDetailsSubmitted ? (
                                  <button
                                    className="btn shadow-sm"
                                    style={{
                                      fontSize:
                                        "clamp(0.625rem, 1.5vw, 0.75rem)",
                                      color: "#ffffff",
                                      backgroundColor: "#ff6b35",
                                      border: "1px solid #ff5722",
                                      fontWeight: 600,
                                      padding: "8px 12px",
                                    }}
                                    onClick={() =>
                                      history.push({
                                        pathname: `/auction_representative/user_home/manage-ta-onboard/update-ta-user:${value?.taOnboardingCaseId}`,
                                        state: {
                                          data: value,
                                        },
                                      })
                                    }
                                  >
                                    <i className="bi bi-pencil-square me-1"></i>
                                    Enter Business Details
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-light shadow-sm"
                                    style={{
                                      fontSize: "10px",
                                      color: "rgb(40, 180, 255)",
                                      backgroundColor: "rgb(254, 254, 254)",
                                    }}
                                    onClick={() => handleViewDetails(value)}
                                  >
                                    View Details
                                  </button>
                                )}
                              </div>
                              {user?.role === "Business Analyst" && (
                                <>
                                  <div
                                    className=""
                                    style={{ marginTop: "10px" }}
                                  >
                                    <button
                                      className="btn btn-light shadow-sm"
                                      style={{
                                        fontSize: "10px",
                                        color: "rgb(40, 180, 255)",
                                        backgroundColor: "rgb(254, 254, 254)",
                                      }}
                                      onClick={() => {
                                        setModalAddComment(true);
                                        setModalAddCommentData(value);
                                      }}
                                    >
                                      Add Comment
                                    </button>
                                  </div>
                                  {user?.role === "Business Analyst" &&
                                    !value?.isCaseApprovedByBa && (
                                      <div
                                        className=""
                                        style={{ marginTop: "10px" }}
                                      >
                                        <button
                                          className="btn btn-light shadow-sm"
                                          style={{
                                            fontSize: "10px",
                                            color: "rgb(40, 180, 255)",
                                            backgroundColor:
                                              "rgb(254, 254, 254)",
                                          }}
                                          onClick={(e) => {
                                            handleVerifyAll(
                                              e,
                                              value?.taOnboardingCaseId,
                                              "approve",
                                            );
                                          }}
                                        >
                                          All Verified
                                        </button>
                                      </div>
                                    )}

                                  {!value?.isLosApplicationIdUpdated &&
                                    value?.isCaseApprovedByBa && (
                                      <div
                                        className=""
                                        style={{ marginTop: "10px" }}
                                      >
                                        <button
                                          className="btn btn-light shadow-sm"
                                          style={{
                                            fontSize: "10px",
                                            color: "rgb(40, 180, 255)",
                                            backgroundColor:
                                              "rgb(254, 254, 254)",
                                          }}
                                          // onClick={setModal(true)}
                                          onClick={() => {
                                            setModal(true);
                                            setModalData(value);
                                          }}
                                        >
                                          LOS Details
                                        </button>
                                      </div>
                                    )}
                                  {value?.isLosApplicationIdUpdated &&
                                    value?.isCaseApprovedByBa === true &&
                                    !value?.isLimitSettingAllDocumentUploaded && (
                                      <div
                                        className=""
                                        style={{ marginTop: "10px" }}
                                      >
                                        <button
                                          className="btn btn-light shadow-sm"
                                          style={{
                                            fontSize: "10px",
                                            color: "rgb(40, 180, 255)",
                                            backgroundColor:
                                              "rgb(254, 254, 254)",
                                          }}
                                          onClick={() =>
                                            history.push({
                                              pathname:
                                                user.role ===
                                                "Auction Representative"
                                                  ? "/dashboard/trade_advance/onboarding/limit_setting_document_collection"
                                                  : "/limit_setting_document_collection",
                                              state: {
                                                value: value,
                                              },
                                            })
                                          }
                                        >
                                          Limit Setting Doc
                                        </button>
                                      </div>
                                    )}
                                </>
                              )}
                              {user?.role !== "Business Analyst" && (
                                <>
                                  <div
                                    className=""
                                    style={{ marginTop: "10px" }}
                                  >
                                    <button
                                      className="btn btn-light shadow-sm"
                                      style={{
                                        fontSize: "10px",
                                        color: "rgb(40, 180, 255)",
                                        backgroundColor: "rgb(254, 254, 254)",
                                      }}
                                      onClick={() => {
                                        setModalAddComment(true);
                                        setModalAddCommentData(value);
                                      }}
                                    >
                                      View Comments
                                    </button>
                                  </div>

                                  {/* {!value?.isLosApplicationIdUpdated &&
                              value?.isCaseApprovedByBa && (
                                <div className="" style={{ marginTop: "10px" }}>
                                  <button
                                    className="btn btn-light shadow-sm"
                                    style={{
                                      fontSize: "10px",
                                      color: "rgb(40, 180, 255)",
                                      backgroundColor: "rgb(254, 254, 254)",
                                    }}
                                    // onClick={setModal(true)}
                                    onClick={() => {
                                      setModal(true);
                                      setModalData(value);
                                    }}
                                  >
                                    LOS Application Id
                                  </button>
                                </div>
                              )}
                            {value?.isLosApplicationIdUpdated &&
                              value?.isCaseApprovedByBa === true && (
                                <div className="" style={{ marginTop: "10px" }}>
                                  <button
                                    className="btn btn-light shadow-sm"
                                    style={{
                                      fontSize: "10px",
                                      color: "rgb(40, 180, 255)",
                                      backgroundColor: "rgb(254, 254, 254)",
                                    }}
                                    onClick={() =>
                                      history.push({
                                        pathname:
                                          "/limit_setting_document_collection",
                                        state: {
                                          value: value,
                                        },
                                      })
                                    }
                                  >
                                    Limit Setting Doc
                                  </button>
                                </div>
                              )} */}
                                </>
                              )}
                              {user?.role === "Business Analyst" &&
                                value?.isLimitSettingAllDocumentUploaded &&
                                !value?.isEmailSentByRisk && (
                                  <>
                                    <div
                                      className=""
                                      style={{ marginTop: "10px" }}
                                    >
                                      <button
                                        className="btn btn-light shadow-sm"
                                        style={{
                                          fontSize: "10px",
                                          color: "rgb(40, 180, 255)",
                                          backgroundColor: "rgb(254, 254, 254)",
                                        }}
                                        onClick={() => {
                                          handleSendEmail(value);
                                        }}
                                      >
                                        Send Email
                                      </button>
                                    </div>
                                    <div
                                      className=""
                                      style={{ marginTop: "10px" }}
                                    >
                                      <button
                                        className="btn btn-light shadow-sm"
                                        style={{
                                          fontSize: "10px",
                                          color: "rgb(40, 180, 255)",
                                          backgroundColor: "rgb(254, 254, 254)",
                                        }}
                                        onClick={() => {
                                          handleConformEmail(value);
                                        }}
                                      >
                                        Email Send Confirmation
                                      </button>
                                    </div>
                                  </>
                                )}

                              {(user?.role === "Back Office HR" ||
                                user?.role === "Super Admin" ||
                                user?.role === "Developer") &&
                                value?.isLimitSettingAllDocumentUploaded &&
                                !value?.is_limit_setting_done && (
                                  <>
                                    <div
                                      className=""
                                      style={{ marginTop: "10px" }}
                                    >
                                      <button
                                        className="btn btn-light shadow-sm"
                                        style={{
                                          fontSize: "10px",
                                          color: "rgb(40, 180, 255)",
                                          backgroundColor: "rgb(254, 254, 254)",
                                        }}
                                        onClick={() => {
                                          setModalForUpdateLimit(true);
                                          setModalForUpdateLimitData(value);
                                        }}
                                      >
                                        Update Limit Setting
                                      </button>
                                    </div>
                                  </>
                                )}
                            </>
                          ) : value?.adminApprovalStatus === "rejected" ? (
                            <div
                              className="d-flex align-items-start justify-content-end w-100 mt-2"
                              style={{ minHeight: "40px" }}
                            >
                              <div
                                className="p-3 rounded shadow-sm"
                                style={{
                                  backgroundColor: "rgb(254,242,242)",
                                  border: "1px solid rgb(239,200,200)",
                                  maxWidth: "100%",
                                }}
                              >
                                <div className="d-flex align-items-start mb-2">
                                  <i
                                    className="bi bi-x-circle-fill me-2 flex-shrink-0"
                                    style={{
                                      color: "rgb(200,39,39)",
                                      fontSize: "clamp(1rem, 2.5vw, 1.25rem)",
                                      marginTop: "2px",
                                    }}
                                  ></i>
                                  <div className="flex-grow-1">
                                    <div
                                      style={{
                                        color: "rgb(200,39,39)",
                                        fontWeight: 600,
                                        fontSize: "clamp(0.875rem, 2vw, 1rem)",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      Admin Rejected
                                    </div>
                                    <div
                                      style={{
                                        color: "rgb(120,20,20)",
                                        fontSize:
                                          "clamp(0.813rem, 1.5vw, 0.938rem)",
                                        lineHeight: "1.5",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      <strong
                                        style={{ color: "rgb(150,30,30)" }}
                                      >
                                        Reason:
                                      </strong>{" "}
                                      {value?.adminApprovalRejectionReason ||
                                        "No reason provided"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-end w-100 mt-2"
                              style={{ minHeight: "40px" }}
                            >
                              <div
                                className="px-3 py-2 rounded"
                                style={{
                                  backgroundColor: "rgb(255, 251, 235)",
                                  border: "1px solid rgb(255, 213, 153)",
                                }}
                              >
                                <i
                                  className="bi bi-clock-history me-2"
                                  style={{
                                    color: "rgb(201, 131, 83)",
                                    fontSize: "clamp(0.875rem, 2vw, 1rem)",
                                  }}
                                ></i>
                                <span
                                  style={{
                                    color: "rgb(201, 131, 83)",
                                    fontWeight: 500,
                                    fontSize: "clamp(0.75rem, 1.8vw, 0.875rem)",
                                  }}
                                >
                                  Awaiting Admin Approval
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {user?.role !== "Super Admin" &&
                        user?.role !== "Developer" &&
                        user?.role !== "Back Office HR" &&
                        user?.role !== "Head - Risk And Process" && (
                          <div className="col-12 mt-1">
                            <span
                              className=" text-sm text-secondary"
                              style={
                                {
                                  // fontSize: "13px",
                                  // color: "rgb(192,190,190)",
                                }
                              }
                            >
                              {`${value?.pending} of ${value?.totalDoc} Documents need attention`}{" "}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                ))}

              {!loading &&
                !refreshing &&
                Object.entries(filterData).length === 0 && (
                  <div
                    className="text-center text-danger mt-2"
                    style={{ minHight: "100rem", maxHeight: "150rem" }}
                  >
                    No Data Found
                  </div>
                )}

              {/* the last card sat flush against the bottom of the viewport */}
              <div style={{ height: 56 }} />
            </div>
          </>
        )}
      </>
    );
  } catch (error) {
    console.log({ error });
    return <ErrorHandler error={error} />;
  }
};

export default OnBoardingView;
